import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Search, Target, Send } from "lucide-react";
import Footer from "@/components/Footer";

const GetGrowthScoreThankYouPage = () => {
  const location = useLocation();
  const state = location.state as { name?: string; email?: string } | null;
  const name = state?.name;
  const email = state?.email;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fire FB Pixel via dataLayer
  useEffect(() => {
    if (typeof window !== "undefined") {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: "CompleteRegistration",
        content_name: "Growth Checkup",
        currency: "USD",
      });
    }
  }, []);

  const steps = [
    { icon: Search, text: "We review your website, visibility, and core new-patient funnel." },
    { icon: Target, text: "We identify the biggest issues and the clearest next priorities." },
    { icon: Send, text: "We send your checkup and, if helpful, outline what the right next step would look like." },
  ];

  return (
    <div className="min-h-screen bg-hero-bg">
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto section-padding">
          <div className="text-center mb-10">
            <CheckCircle2 className="w-14 h-14 text-accent mx-auto mb-5" />
            <h1 className="text-2xl md:text-3xl font-bold text-hero-foreground mb-3">
              Your Growth Checkup Request Is In
            </h1>
            <p className="text-hero-foreground/60 leading-relaxed max-w-lg mx-auto">
              {name ? `Thank you, ${name}. ` : "Thank you. "}
              Our team will review your practice and prepare your New Patient Growth Checkup. Most checkups are delivered within 3 business days, depending on queue volume and complexity.
            </p>
          </div>

          {/* What happens next */}
          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg mb-6">
            <p className="font-semibold text-foreground mb-5">What Happens Next</p>
            <div className="space-y-5">
              {steps.map((s, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-9 h-9 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                    <s.icon className="w-4 h-4 text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-0.5">Step {i + 1}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {email && (
            <div className="bg-card rounded-2xl p-6 border border-border shadow-lg mb-6">
              <p className="text-sm text-muted-foreground leading-relaxed">
                We'll send the results to <span className="font-semibold text-accent">{email}</span>. Keep an eye on your inbox.
              </p>
            </div>
          )}

          {/* Optional scheduling */}
          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg text-center mb-6">
            <h2 className="text-lg font-bold text-foreground mb-2">
              Want to Review Your Checkup Live?
            </h2>
            <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
              If you would like to review your checkup live once it is ready, you can reserve a time below.
            </p>
            <a
              href="https://api.leadconnectorhq.com/widget/bookings/meetwithhans"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg text-base font-semibold hover:bg-accent/90 transition-all shadow-lg shadow-accent/25"
            >
              Reserve a Review Time
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          <div className="text-center">
            <a
              href="https://www.xpresspromotion.com"
              className="text-hero-foreground/40 text-sm hover:text-hero-foreground/60 transition-colors"
            >
              ← Return to Xpress Website
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default GetGrowthScoreThankYouPage;
